import { format, addDays } from 'date-fns';

// Availability types and constants

export const AVAILABILITY_TYPES = {
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
  PREFERRED: 'preferred',
};

export const TIME_SLOTS = [
  '07:00',
  '07:30',
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
  '18:00',
  '18:30',
  '19:00',
  '19:30',
  '20:00',
  '20:30',
  '21:00',
  '21:30',
];

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const DAYS_OF_WEEK_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const AVAILABILITY_STYLES = {
  [AVAILABILITY_TYPES.AVAILABLE]: 'bg-blue-200 hover:bg-blue-300 border-blue-400',
  [AVAILABILITY_TYPES.UNAVAILABLE]: 'bg-red-200 hover:bg-red-300 border-red-400',
  [AVAILABILITY_TYPES.PREFERRED]: 'bg-green-200 hover:bg-green-300 border-green-400',
};

export const AVAILABILITY_LABELS = {
  [AVAILABILITY_TYPES.AVAILABLE]: 'Available',
  [AVAILABILITY_TYPES.UNAVAILABLE]: 'Unavailable',
  [AVAILABILITY_TYPES.PREFERRED]: 'Preferred',
};

/** True when this half-hour slot was never saved (missing key ≠ explicitly unavailable). */
export const isAvailabilitySlotUnset = (dayData, timeSlot) => {
  const v = dayData?.[timeSlot];
  return v === undefined || v === null || v === '';
};

/** Short label for tooltips: "Not set" vs Available / Unavailable / Preferred. */
export const getSlotStateLabel = (dayData, timeSlot) => {
  const v = dayData?.[timeSlot];
  if (v === undefined || v === null || v === '') return 'Not set';
  return AVAILABILITY_LABELS[v] ?? String(v);
};

// Preset configurations for common availability patterns
export const AVAILABILITY_PRESETS = {
  morning: {
    name: 'Morning Sessions',
    description: 'Available for morning training (7:00-10:00)',
    pattern: (dayIndex, timeIndex) => {
      return timeIndex < 8 ? AVAILABILITY_TYPES.AVAILABLE : null;
    },
  },
  afternoon: {
    name: 'Afternoon Sessions',
    description: 'Available for afternoon training (14:00-18:00)',
    pattern: (dayIndex, timeIndex) => {
      return timeIndex >= 16 && timeIndex < 24 ? AVAILABILITY_TYPES.AVAILABLE : null;
    },
  },
  evening: {
    name: 'Evening Sessions',
    description: 'Available for evening training (18:00-21:00)',
    pattern: (dayIndex, timeIndex) => {
      return timeIndex >= 24 && timeIndex < 30 ? AVAILABILITY_TYPES.AVAILABLE : null;
    },
  },
  weekdays: {
    name: 'Weekdays Only',
    description: 'Available Monday through Friday',
    pattern: (dayIndex, _timeIndex) => {
      return dayIndex < 5 ? AVAILABILITY_TYPES.AVAILABLE : null;
    },
  },
  weekends: {
    name: 'Weekends Only',
    description: 'Available Saturday and Sunday',
    pattern: (dayIndex, _timeIndex) => {
      return dayIndex >= 5 ? AVAILABILITY_TYPES.AVAILABLE : null;
    },
  },
  clear: {
    name: 'Clear All',
    description: 'Clear all availability',
    pattern: (_dayIndex, _timeIndex) => null,
  },
};

// Utility functions
export const formatTimeSlot = (timeSlot) => {
  if (!timeSlot) return '';
  return timeSlot.substring(0, 5); // Remove seconds if present
};

export const formatWeekRange = (weekStart) => {
  if (!weekStart) return '';
  const weekEnd = addDays(weekStart, 6);
  return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
};

export const parseTimeSlot = (timeString) => {
  if (!timeString) return null;
  // Ensure format is HH:MM:SS
  if (timeString.length === 5) {
    return `${timeString}:00`;
  }
  return timeString;
};

export const getTimeSlotIndex = (timeSlot) => {
  const formatted = formatTimeSlot(timeSlot);
  return TIME_SLOTS.indexOf(formatted);
};

export const getDayIndex = (dayName) => {
  return DAYS_OF_WEEK.indexOf(dayName);
};

// Convert between frontend grid format and backend API format
export const convertToApiFormat = (gridAvailability, weekStart) => {
  const slots = [];

  gridAvailability.forEach((day, dayIndex) => {
    day.forEach((availability, timeIndex) => {
      if (availability) {
        const date = new Date(weekStart);
        date.setDate(date.getDate() + dayIndex);

        slots.push({
          date: date.toISOString().split('T')[0],
          time_slot: parseTimeSlot(TIME_SLOTS[timeIndex]),
          availability_type: availability,
        });
      }
    });
  });

  return slots;
};

export const convertFromApiFormat = (apiSlots) => {
  // Initialize empty grid: 7 days x 30 time slots
  const grid = Array(7)
    .fill(null)
    .map(() => Array(30).fill(null));

  apiSlots.forEach((slot) => {
    const date = new Date(slot.date);
    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1; // Convert Sunday=0 to Sunday=6
    const timeIndex = getTimeSlotIndex(formatTimeSlot(slot.time_slot));

    if (dayIndex >= 0 && dayIndex < 7 && timeIndex >= 0 && timeIndex < 30) {
      grid[dayIndex][timeIndex] = slot.availability_type;
    }
  });

  return grid;
};

export const getWeekDates = (weekStart) => {
  if (!weekStart) return [];

  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + i);
    dates.push(date);
  }
  return dates;
};

export const getWeeklyStats = (availability) => {
  if (!availability || typeof availability !== 'object') {
    return {
      daysWithAvailability: 0,
      allDaysCovered: false,
      availableSlots: 0,
      preferredSlots: 0,
      unavailableSlots: 0,
      totalSlots: 0,
      // Alias properties for mobile component compatibility
      available: 0,
      preferred: 0,
      total: 0,
    };
  }

  let daysWithAvailability = 0;
  let availableSlots = 0;
  let preferredSlots = 0;
  let unavailableSlots = 0;
  let totalSlots = 0;

  // Handle the object format: { dayIndex: { timeSlot: availabilityType } }
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayData = availability[dayIndex];
    let dayHasAvailability = false;

    if (dayData && typeof dayData === 'object') {
      Object.values(dayData).forEach((availabilityType) => {
        if (availabilityType && availabilityType !== AVAILABILITY_TYPES.UNAVAILABLE) {
          totalSlots++;
          dayHasAvailability = true;

          switch (availabilityType) {
            case AVAILABILITY_TYPES.AVAILABLE:
              availableSlots++;
              break;
            case AVAILABILITY_TYPES.PREFERRED:
              preferredSlots++;
              break;
            case AVAILABILITY_TYPES.UNAVAILABLE:
              unavailableSlots++;
              break;
          }
        }
      });
    }

    if (dayHasAvailability) {
      daysWithAvailability++;
    }
  }

  // Calculate total hours (each slot is 30 minutes = 0.5 hours)
  const totalHours = Math.round(totalSlots * 0.5 * 10) / 10; // Round to 1 decimal

  return {
    daysWithAvailability,
    allDaysCovered: daysWithAvailability === 7,
    availableSlots,
    preferredSlots,
    unavailableSlots,
    totalSlots,
    // Alias properties for mobile component compatibility
    available: availableSlots,
    preferred: preferredSlots,
    total: totalHours,
  };
};

export const calculateAvailabilityStats = (gridAvailability) => {
  let totalSlots = 0;
  let availableSlots = 0;
  let preferredSlots = 0;
  let unavailableSlots = 0;

  gridAvailability.forEach((day) => {
    day.forEach((availability) => {
      if (availability) {
        totalSlots++;
        switch (availability) {
          case AVAILABILITY_TYPES.AVAILABLE:
            availableSlots++;
            break;
          case AVAILABILITY_TYPES.PREFERRED:
            preferredSlots++;
            break;
          case AVAILABILITY_TYPES.UNAVAILABLE:
            unavailableSlots++;
            break;
        }
      }
    });
  });

  return {
    total: totalSlots,
    available: availableSlots,
    preferred: preferredSlots,
    unavailable: unavailableSlots,
    percentageAvailable:
      totalSlots > 0 ? Math.round(((availableSlots + preferredSlots) / totalSlots) * 100) : 0,
  };
};
